// Frontend mirror of `shared/src/dto.rs` + `shared/src/actions/*` +
// `shared/src/error.rs`. Hand-maintained — change this file in lockstep
// with the Rust DTOs.
//
// The Rust side has serialization tests (see `shared/src/dto.rs::tests`
// and `shared/src/actions/params.rs::tests`) that pin the wire format
// against the wallet libraries (`@hot-labs/near-connect`, viem, etc.).
// As long as those tests pass, the types here are accurate; a change
// that breaks them surfaces as either a Rust test failure (canonical)
// or a TS `tsc` failure (consumer).
//
// Why not auto-generate (e.g. ts-rs)? The shared crate must compile to
// wasm32-unknown-unknown cleanly and stay tiny; adding `ts-rs` doubles
// its build footprint for ~25 types we can keep in sync by hand. The
// serialization tests on the Rust side are the actual contract.

// ─── Connectors ──────────────────────────────────────────────────────────

/** kebab-case wire form of `shared::ConnectorId`. */
export type ConnectorId =
  | "omni-bridge"
  | "btc-connector"
  | "zcash-connector"
  | "intents"
  | "intents-far"
  | "eth-light-client"
  | "btc-light-client"
  | "zcash-light-client"
  | "eth-connector";

export const CONNECTOR_IDS: readonly ConnectorId[] = [
  "omni-bridge",
  "btc-connector",
  "zcash-connector",
  "intents",
  "intents-far",
  "eth-light-client",
  "btc-light-client",
  "zcash-light-client",
  "eth-connector",
];

// ─── Health + connector listing ─────────────────────────────────────────

export interface HealthResponse {
  network: string;
  version: string;
  connectors_loaded: number;
}

export interface ConnectorInfo {
  id: ConnectorId;
  contract: string;
  label: string;
  utxo_chain: string | null;
  pausable_features: string[];
}

// ─── Relayer status ─────────────────────────────────────────────────────

/** Internally-tagged enum per `RelayerStatus` in dto.rs. */
export type RelayerStatus =
  | { kind: "trusted" }
  | { kind: "pending"; activate_at_ns?: string }
  | { kind: "not_trusted" };

export interface CheckRelayerResponse {
  account_id: string;
  per_connector: Partial<Record<ConnectorId, RelayerStatus>>;
}

// ─── Pause ──────────────────────────────────────────────────────────────

export interface PauseStatusResponse {
  connector: ConnectorId;
  /** `true` when the near-plugins `ALL` wildcard is set (everything paused via
   *  the single master switch). Drives the "Pause all" / "Unpause all" toggle. */
  all_paused: boolean;
  /** Per configured feature → individually paused (its own key in the paused
   *  set), independent of `all_paused`. */
  features: Record<string, boolean>;
}

export interface UnsignedTxsResponse {
  unsignedTxs: UnsignedNearTx[];
}

// ─── Unsigned txs (all chains) ──────────────────────────────────────────

export interface UnsignedNearTx {
  receiverId: string;
  actions: NearAction[];
}

export type NearAction =
  | { type: "FunctionCall"; params: FunctionCallParams }
  | { type: "Transfer"; params: TransferParams };

export interface FunctionCallParams {
  methodName: string;
  args: unknown;
  /** Decimal string (gas in yocto-gas units). */
  gas: string;
  /** Decimal string (yoctoNEAR). */
  deposit: string;
}

export interface TransferParams {
  deposit: string;
}

export interface UnsignedEvmTx {
  /** 0x-hex chainId (matches EIP-1193 `wallet_switchEthereumChain`). */
  chainId: string;
  to: string;
  data: string;
  value: string;
  gas?: string;
}

export interface SolanaAccountMeta {
  pubkey: string;
  isSigner: boolean;
  isWritable: boolean;
}

export interface UnsignedSolanaTx {
  programId: string;
  accounts: SolanaAccountMeta[];
  dataBase64: string;
  clusterRpcUrl: string;
}

export interface UnsignedStarknetTx {
  to: string;
  selectorName: string;
  selector: string;
  calldata: string[];
  chainId: string;
}

export type ActionBuildResponse =
  | { kind: "unsigned_txs"; unsignedTxs: UnsignedNearTx[] }
  | { kind: "unsigned_evm_tx"; evmTx: UnsignedEvmTx }
  | { kind: "unsigned_solana_tx"; solanaTx: UnsignedSolanaTx }
  | { kind: "unsigned_starknet_tx"; starknetTx: UnsignedStarknetTx }
  | { kind: "view_result"; result: unknown };

// ─── Action registry ────────────────────────────────────────────────────

/** kebab-case wire form of `shared::ActionKind`. */
export type ActionKind =
  | "log-metadata"
  | "deploy-token"
  | "bind-token"
  | "near-storage-deposit"
  | "near-sign-transfer"
  | "near-init-transfer"
  | "near-fin-transfer"
  | "near-fast-fin-transfer"
  | "near-fast-fin-transfer-from-utxo"
  | "evm-fin-transfer"
  | "sol-fin-transfer"
  | "starknet-fin-transfer"
  | "is-transfer-finalised"
  | "near-sign-btc-transaction"
  | "near-submit-btc-transfer"
  | "near-fin-transfer-btc"
  | "btc-verify-withdraw"
  | "btc-rbf-increase-gas-fee"
  | "btc-cancel-withdraw"
  | "btc-verify-active-utxo-management"
  | "btc-request-refund"
  | "btc-verify-refund-finalize"
  | "btc-fin-transfer"
  | "active-utxo-management"
  | "get-bitcoin-address"
  | "init-near-to-bitcoin-transfer";

export type ActionCategory =
  | "initialize"
  | "sign"
  | "finalize"
  | "maintenance"
  | "diagnostic";

export type ResultKind =
  | "returns_unsigned_tx"
  | "returns_unsigned_txs"
  | "returns_view";

export type ParamKind =
  | { type: "account_id" }
  | { type: "u128" }
  | { type: "u64" }
  | { type: "chain_kind" }
  | { type: "near_tx_hash" }
  | { type: "btc_tx_hash" }
  | { type: "omni_address" }
  | { type: "string"; max_len: number };

// On the wire serde emits unit variants of `ParamKind` as their bare
// lowercase tag (e.g. `"account_id"`) and the `String { max_len }`
// variant as `{ "string": { max_len: 1024 } }`. Match either.
export type ParamKindWire =
  | "account_id"
  | "u128"
  | "u64"
  | "chain_kind"
  | "near_tx_hash"
  | "btc_tx_hash"
  | "omni_address"
  | { string: { max_len: number } };

/** Subset of `ChainKind` variants the form should offer in a chain
 *  dropdown — wire form is the PascalCase variant name (`Btc`, `Eth`).
 *  `undefined` keeps the full default set. Only meaningful when
 *  `kind == "chain_kind"`. */
export type ChainKindName =
  | "Near"
  | "Eth"
  | "Sol"
  | "Arb"
  | "Base"
  | "Bnb"
  | "Polygon"
  | "Abs"
  | "Btc"
  | "Zcash"
  | "Starknet"
  | "Fogo"
  | "Ton"
  | "Sui";

export interface ParamSpec {
  name: string;
  label: string;
  kind: ParamKindWire;
  required: boolean;
  help?: string;
  placeholder?: string;
  allowed_chains?: ChainKindName[];
}

export interface ActionDescriptor {
  kind: ActionKind;
  slug: string;
  label: string;
  help: string;
  category: ActionCategory;
  connectors: ConnectorId[];
  params: ParamSpec[];
  result_kind: ResultKind;
}

// ─── Errors ─────────────────────────────────────────────────────────────

export type ApiErrorCode =
  | "INVALID_PARAM"
  | "UNKNOWN_ACTION"
  | "UNKNOWN_CONNECTOR"
  | "UNKNOWN_FEATURE"
  | "CONFIG_ERROR"
  | "RPC_ERROR"
  | "BUILDER_ERROR"
  | "NEEDS_EXTERNAL_PROOF"
  | "INTERNAL";

export interface ApiError {
  code: ApiErrorCode;
  message: string;
  details?: unknown;
}

export interface ApiErrorBody {
  error: ApiError;
}

// ─── Helpers ────────────────────────────────────────────────────────────

/** Read a [`ParamKindWire`] into a tagged-union form for downstream switch. */
export function paramKind(wire: ParamKindWire): ParamKind {
  if (typeof wire === "string") return { type: wire };
  return { type: "string", max_len: wire.string.max_len };
}
